import { useMemo, useContext, useState } from "react";
import { useTable } from "react-table";
import { truncateString } from "../../../../helpers";
import primaryComponents from "../../../primaryComponents";

import globalStyles from "../../../../assets/styles/base/globalPage.module.scss";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";
import { pathConstants } from "../../../../routes/pathContants";
import secondaryComponents from "../..";
import { useDeleteRestockDraftMutation } from "../../../../hooks/api/mutations/useInventory";
import { ToastContext } from "../../../../hooks/context";

const useRestockDrafts = ({ restockDrafts = [] }) => {
  const navigate = useNavigate();
  const triggerToast = useContext(ToastContext);
  const deleteRestockDraftMutation = useDeleteRestockDraftMutation();
  const [selectedDraftToDelete, setSelectedDraftToDelete] = useState(null);

  const handleConfirmDeleteDraft = () => {
    if (!selectedDraftToDelete?.id) return;
    deleteRestockDraftMutation.mutate(selectedDraftToDelete?.id, {
      onSuccess: () => {
        triggerToast("Draft deleted successfully", "success");
        setSelectedDraftToDelete(null);
      },
    });
  };

  const columns = useMemo(
    () => [
      {
        Header: "Restock ID",
        accessor: "id",
        Cell: ({ cell: { value } }) => <span>{value}</span>,
      },
      {
        Header: "Supplier",
        accessor: "suppliers",
        Cell: ({ cell: { value } }) => value?.[0]?.name || "N/A",
      },

      {
        Header: "Store",
        accessor: "stores",
        Cell: ({ cell: { value: stores } }) => {
          if (stores?.length > 1) {
            return (
              <span>
                <secondaryComponents.ToolTip
                  placeholderContent="Multiple Stores"
                  showInfoIcon
                  useContentStyling
                  contentStyles={{
                    width: "100%",
                    maxWidth: "200px",
                    padding: "15px",
                  }}
                >
                  <span
                    className={`${globalStyles.rowSpaceBetween} ${globalStyles.borderBottom} pb-2`}
                  >
                    <p className="smallest-text-size">Store Name</p>
                  </span>
                  <span>
                    {stores.map((store) => (
                      <div
                        className={`${globalStyles.rowSpaceBetween} ${globalStyles.borderBottom} pt-2 pb-2`}
                        key={uuidv4()}
                      >
                        <p className="smallest-text-size">
                          {truncateString(store?.name, 20)}
                        </p>
                      </div>
                    ))}
                  </span>
                </secondaryComponents.ToolTip>
              </span>
            );
          }
          return (
            <span className="text-muted">{stores?.[0]?.name || "N/A"}</span>
          );
        },
      },

      {
        Header: "Action",
        Cell: ({ row }) => (
          <span className="d-flex" style={{ gap: "8px" }}>
            <primaryComponents.Button
              onClick={() => {
                navigate(
                  pathConstants.INVENTORY_PRODUCTS_BULK_RESTOCK_UPDATE({
                    restock_id: row?.original?.id,
                  }),
                );
              }}
              classNames={"btn btn--outline smallBtn"}
            >
              Continue
            </primaryComponents.Button>
            <primaryComponents.Button
              onClick={() => setSelectedDraftToDelete(row?.original)}
              classNames={"btn btn--outline-red smallBtn"}
            >
              Delete
            </primaryComponents.Button>
          </span>
        ),
      },
    ],
    [],
  );

  const instance = useTable({
    columns: columns,
    data: restockDrafts,
  });

  return {
    instance,
    selectedDraftToDelete,
    setSelectedDraftToDelete,
    handleConfirmDeleteDraft,
    isDeletingDraft: deleteRestockDraftMutation.isLoading,
  };
};

export default useRestockDrafts;
